import { ReactElement, memo, useCallback, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SlotInfo } from "react-big-calendar";
import { SxProps } from "@mui/material";
import { Leader, MeetingSlot, RootState, ThunkDispatchType, WeekStartDay } from "../../../store";
import { DateTime } from "luxon";
import { Meeting, NewMeeting, UICalendarEventConsolidated } from "../../../store/schedule/types";
import CalendarScheduler from "./CalendarScheduler";
import { scheduling } from "../../../store/cabinetApi/scheduling";
import { PAGE_URL } from "../../../constants";
import { 
  selectCalendarLookupByCalendarId, 
  selectUserCalendarEventsFromRTK, 
  selectLeaderLookup 
} from "../../../store/schedule/selectors";
import { TimeZone, getTimeZone } from "../../../utils/scheduleUtils";
import { router } from "../../../router";
import {
  selectCalendarMap,
  selectSelectedAdditionalCalendars, selectSelectedLeaderCalendars, updateSecondaryTimezones,
  updateCalendarTimezone, selectDateRange, selectCurrentOrNewMeeting, updateDateRange,
  selectActiveTimezones,
  selectAllHiddenCalendarIds,
  selectActiveCalendars,
  selectMeetingPrimaryTimezoneOverridesScheduleDefaults,
  selectMeetingSecondaryTimezonesOverrideScheduleDefaults,
} from "../../../store/scheduleUI";
import { useHandler } from "../../../store/hooks";
import { View as RBCView } from "react-big-calendar";
import { checkForCalendarGrants } from "../../../utils/authUtils";
import { useConfirmationModalWithUI } from "../../../components/Common/ConfirmationModal";
import { isEqual } from "lodash-es";


const pollEventsIntervalMs = Number(import.meta.env.VITE_POLL_EVENTS_INTERVAL_SECONDS) * 1000;


/** True if applying this primary zone would change what is stored on the meeting. */
export function meetingPrimaryTimezoneWouldChange(meeting: Meeting | NewMeeting, newTimezoneName: string): boolean {
  return meeting.calendar_tz !== newTimezoneName;
}

/** True if applying these secondary zone names would change what is stored on the meeting. */
export function meetingSecondaryTimezonesWouldChange(
  meeting: Meeting | NewMeeting, newSecondaryNames: string[]
): boolean {
  const onMeeting = new Set((meeting.secondary_tz || []).filter((t): t is string => t != null));
  return !isEqual(onMeeting, new Set(newSecondaryNames));
}


type Props =  {
  selectedLeaders: number[];
  handleOpenMeeting?: (meetingId: number) => void;
  selectedSlots: MeetingSlot[];
  recurringSlots: MeetingSlot[];
  handleOpenRecurrenceIndex?: (index: number) => void;
  handleDeleteRecurrenceIndex?: (index: number) => void;
  handleEditRecurrenceIndex?: (index: number, start: Date, end: Date) => void;
  handleSlotsCreated?: (info: SlotInfo, isExcluded?: boolean) => void;
  handleEditSlot?: (eventId: string, start: Date, end: Date, isExcluded: boolean) => void;
  handleDeleteSlots?: (slots: MeetingSlot[]) => void;
  handleDuplicateMeeting?: (meetingId: number) => void
  handleDeleteMeeting?: (meetingId: number) => void
  handleShareMeeting?: (meetingId: number) => void
  handleCopyLink?: (meetingId: number) => void
  hideCalendarHeader?: boolean;
  hideTodayButton?: boolean;
  hideViewPicker?: boolean;
  noCalendarBorder?: boolean;
  hideResourceHeader?: boolean;
  hideTimezonePicker?: boolean;
  interactive?: boolean;
  sx?: SxProps;
  allowedDates?: DateTime[];
  defaultCalendarView?: RBCView;
  
  // Event fetching parameters
  googleCalIds: string[];
  msCalIds: string[];
  forTimezone: string;
  showCanceledDeclined?: boolean;
  prefetchNextPeriod?: boolean;
  
  // Event data callbacks
  onEventsLoaded?: (events: UICalendarEventConsolidated[]) => void;
  onLoadingChange?: (loading: boolean) => void;
  onError?: (error: unknown) => void;

  /** Required when controlled. Uncontrolled: date range from Redux. */
  startDate?: DateTime;
  endDate?: DateTime;
  onDateRangeChange?: (start: DateTime, end: DateTime) => void;
};

const CalendarSchedulerContainer = ({
  selectedLeaders, handleOpenMeeting, selectedSlots, recurringSlots, handleOpenRecurrenceIndex,
  handleDeleteRecurrenceIndex, handleEditRecurrenceIndex, handleSlotsCreated, handleEditSlot, handleDeleteSlots,
  handleDuplicateMeeting, handleDeleteMeeting, handleShareMeeting, handleCopyLink, hideCalendarHeader, hideTodayButton,
  hideViewPicker, noCalendarBorder = true, hideResourceHeader, interactive = true, allowedDates, googleCalIds,
  msCalIds, startDate, endDate, forTimezone, showCanceledDeclined = true, prefetchNextPeriod = false,
  onEventsLoaded, onLoadingChange, onError, onDateRangeChange, sx, defaultCalendarView, hideTimezonePicker = false,
}: Props): ReactElement => {
  const handle = useHandler();
  const dispatch = useDispatch<ThunkDispatchType>();
  const { confirm: confirmMeetingTimezoneChange, ConfirmationModalComponent: MeetingTimezoneConfirmationModal } =
    useConfirmationModalWithUI();
  const navigate = router.navigate;

  const { data: schedulingPrefs } = scheduling.useAppSchedulingPreferencesListQuery();
  const [updateSchedulingPrefs] = scheduling.useAppSchedulingPreferencesPartialUpdateMutation();
  const userPrefs = schedulingPrefs?.user_prefs;
  const { weekStartDay, workWeekStartDay, multiTimezone, granularTimeSelection } = {
    weekStartDay: userPrefs?.week_start_day,
    workWeekStartDay: userPrefs?.work_week_start_day,
    multiTimezone: userPrefs?.multi_timezone,
    granularTimeSelection: userPrefs?.granular_time_selection,
  };
  
  // Convert week_view string to RBCView enum
  const calendarView: RBCView = defaultCalendarView || (userPrefs?.week_view === "WEEK" ? "week" as RBCView
    : userPrefs?.week_view === "DAY" ? "day" as RBCView
      : "work_week" as RBCView);

  const handleCalendarViewChange = async (view: RBCView) => {
    if (userPrefs) {
      // Convert RBCView to week_view string
      const weekView = view === "week" ? "WEEK" 
        : view === "day" ? "DAY" 
          : "WORK_WEEK";
      
      await updateSchedulingPrefs({
        id: userPrefs.id,
        patchedSchedulingPreferences: {
          week_view: weekView,
        }
      });
    }
  };
  
  const user = useSelector((state: RootState) => state.auth.user);
  const leaders = useSelector((state: RootState) => state.leaders);
  const newMeeting = useSelector((state: RootState) => state.schedule.newMeeting);
  const calendars = useSelector((state: RootState) => state.schedule.calendars);
  const meetings = useSelector((state: RootState) => state.schedule.meetings);
  const currentOrNewMeeting = useSelector(selectCurrentOrNewMeeting);
  const calendarMap = useSelector(selectCalendarMap);
  const additionalCalendars = useSelector(selectSelectedAdditionalCalendars);
  const selectedLeaderCalendars = useSelector(selectSelectedLeaderCalendars);
  const showPendingSlots = useSelector((state: RootState) => state.scheduleUI.showPendingSlots);
  const showAllDayEvents = useSelector((state: RootState) => state.scheduleUI.showAllDayEvents);
  const calendarsLoaded = useSelector((state: RootState) => state.schedule.calendarsLoaded);
  const leadersLoaded = useSelector((state: RootState) => state.leaders.loaded);
  const associationsLoaded = useSelector((state: RootState) => state.schedule.associationsLoaded);
  const meetingsLoaded = useSelector((state: RootState) => state.schedule.meetingsLoaded);
  const [calendarTimezone, ...secondaryTimezones] = useSelector(selectActiveTimezones);
  const meetingPrimaryTimezoneOverridesScheduleDefaults = useSelector(
    selectMeetingPrimaryTimezoneOverridesScheduleDefaults
  );
  const meetingSecondaryTimezonesOverrideScheduleDefaults = useSelector(
    selectMeetingSecondaryTimezonesOverrideScheduleDefaults
  );
  const reduxDateRange = useSelector(selectDateRange);
  const activeCalendars = useSelector(selectActiveCalendars);
  const hiddenCalendarIds = useSelector(selectAllHiddenCalendarIds);
  const schedulingDefaultPrimaryTimezoneName = useSelector(
    (state: RootState) => state.scheduleUI.schedulingDefaultPrimaryTimezoneName
  );
  const schedulingDefaultSecondaryTimezoneNames = useSelector(
    (state: RootState) => state.scheduleUI.schedulingDefaultSecondaryTimezoneNames
  );

  const isControlled = onDateRangeChange != null;
  const dateRange = isControlled && startDate != null && endDate != null
    ? { start: startDate, end: endDate }
    : reduxDateRange;
  const currentDateRangeInfo = dateRange;

  const leaderHasAssociations = selectedLeaderCalendars.length > 0;
  const hasGrant = !!user && checkForCalendarGrants(user.oauth_grant_details);

  const leaderLookup = useSelector(selectLeaderLookup);
  const calendarLookup = useSelector(selectCalendarLookupByCalendarId);
  const allCalendars = useSelector((state: RootState) => state.schedule.calendars);

  // Calculate calendar dependency parameter for cache invalidation
  const calendarDependencyParam = useMemo(() => (
    allCalendars.toSorted((a, b) => a.id - b.id)
      .map((cal) => ({ id: cal.id, calendar_id: cal.calendar_id, leaders: cal.leaders }))
      .map((cal) => [cal.id, cal.calendar_id, cal.leaders].join(";")).join(",")
  ), [allCalendars]);

  const visibleDisplayCalendars = useMemo(() => (
    activeCalendars.filter(c => !hiddenCalendarIds.includes(c.id)).map(c => c.calendar_id)
  ), [activeCalendars, hiddenCalendarIds]);

  // Calculate next period dates for prefetching
  const nextPeriodDates = useMemo(() => {
    if (!prefetchNextPeriod) return null;
    
    const duration = dateRange.end.diff(dateRange.start);
    // Start the next period at the beginning of the day after the current period ends
    const nextStart = dateRange.end.plus({ days: 1 }).startOf('day');
    const nextEnd = nextStart.plus(duration);
    
    return { start: nextStart, end: nextEnd };
  }, [prefetchNextPeriod, dateRange.start, dateRange.end]);

  // Use RTK Query data with selectFromResult for optimal performance
  const { data: consolidatedEvents, isLoading, error } = scheduling.useAppSchedulingCalendarEventsListQuery(
    {
      googleCalId: googleCalIds,
      msCalId: msCalIds,
      startDate: dateRange.start.toISO() || '',
      endDate: dateRange.end.toISO() || '',
      forTimezone: forTimezone,
    },
    {
      skip: googleCalIds.length === 0 && msCalIds.length === 0,
      pollingInterval: pollEventsIntervalMs,
      // Refetch if data is older than polling interval in seconds. This makes sure prefetched queries immediately
      // refetch when we switch the view to their period and their last fetch has exceeded the polling interval.
      refetchOnMountOrArgChange: pollEventsIntervalMs / 1000,
      selectFromResult: ({ data, ...other }) => ({
        ...other,
        data: data ? selectUserCalendarEventsFromRTK(
          data.data,
          new Set(visibleDisplayCalendars),
          leaderLookup,
          calendarLookup,
          dateRange.start.toISO() || undefined,
          dateRange.end.toISO() || undefined,
          !showCanceledDeclined
        ) : undefined
      })
    }
  );

  // Prefetch next period if enabled (data is cached for future use)
  scheduling.useAppSchedulingCalendarEventsListQuery(
    {
      googleCalId: googleCalIds,
      msCalId: msCalIds,
      startDate: nextPeriodDates?.start.toISO() || '',
      endDate: nextPeriodDates?.end.toISO() || '',
      forTimezone: forTimezone,
    },
    {
      skip: !nextPeriodDates || (googleCalIds.length === 0 && msCalIds.length === 0),
      selectFromResult: ({ data }) => ({
        data: data ? selectUserCalendarEventsFromRTK(
          data.data,
          new Set(visibleDisplayCalendars),
          leaderLookup,
          calendarLookup,
          nextPeriodDates?.start.toISO() || undefined,
          nextPeriodDates?.end.toISO() || undefined,
          !showCanceledDeclined
        ) : undefined
      })
    }
  );

  // Handle loading state changes
  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  // Handle error state
  useEffect(() => {
    if (error) {
      onError?.(error);
    }
  }, [error, onError]);

  // Invalidate cache when calendar dependency changes (but not during initial loading)
  useEffect(() => {
    if (isLoading) return; // Don't invalidate cache while still loading initial data
    
    if (calendarDependencyParam) {
      dispatch(scheduling.util.invalidateTags(['CalendarEvent']));
    }
  // don't include isLoading here as it will invalidate the cache after initial fetch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, calendarDependencyParam]);

  // Handle events loaded
  useEffect(() => {
    if (consolidatedEvents) {
      onEventsLoaded?.(consolidatedEvents);
    }
  }, [consolidatedEvents, onEventsLoaded]);

  const currentMeeting = currentOrNewMeeting?.id ? meetings[currentOrNewMeeting.id] : null;
  const showSecondaryTimezone = user?.features.MULTI_TIMEZONE && multiTimezone;

  const handleClearMeetingPrimaryTimezoneOverride = useCallback(async () => {
    if (!currentOrNewMeeting?.calendar_tz || schedulingDefaultPrimaryTimezoneName == null) return;
    const tz = getTimeZone(schedulingDefaultPrimaryTimezoneName);
    if (!tz) return;
    dispatch(updateCalendarTimezone(tz));
  }, [currentOrNewMeeting?.calendar_tz, dispatch, schedulingDefaultPrimaryTimezoneName]);

  const handleClearMeetingSecondaryTimezoneOverride = useCallback(
    (filteredSecondaryIndex: number) => {
      if (!currentOrNewMeeting?.calendar_tz) return;

      const defaults = schedulingDefaultSecondaryTimezoneNames;
      const defaultsLength = defaults.length;
      const stz = [...(currentOrNewMeeting.secondary_tz || [])];
      const nonNullCount = stz.filter((t): t is string => t != null).length;

      const applySecondaryNames = (names: string[]) => {
        const tzs = names
          .map((n) => getTimeZone(n))
          .filter((t): t is TimeZone => t != null);
        dispatch(updateSecondaryTimezones(tzs));
      };

      if (nonNullCount > defaultsLength) {
        let nonNullSeen = 0;
        for (let i = 0; i < stz.length; i++) {
          if (stz[i] != null) {
            if (nonNullSeen === filteredSecondaryIndex) {
              stz.splice(i, 1);
              applySecondaryNames(stz.filter((t): t is string => t != null));
              return;
            }
            nonNullSeen++;
          }
        }
        return;
      }

      applySecondaryNames([...defaults]);
    },
    [
      currentOrNewMeeting?.calendar_tz,
      currentOrNewMeeting?.secondary_tz,
      dispatch,
      schedulingDefaultSecondaryTimezoneNames,
    ],
  );
  const currentMeetingDurationMinutes = currentOrNewMeeting ? currentOrNewMeeting.duration_minutes : undefined;
  const currentMeetingAllDaySlots = !!currentOrNewMeeting?.all_day_slots;
  const meetingPreventDoubleBooking = currentOrNewMeeting?.prevent_conflict;

  const openAssociationModal = () => navigate(PAGE_URL.MANAGE_CALENDARS);
  const openAccountManagementModal = () => navigate(PAGE_URL.INTEGRATION_SETTINGS);
  const handleOpenPollResults = (handleOpenPollResultsId: number) => (
    navigate(`${PAGE_URL.POLL_RESULTS}/${handleOpenPollResultsId}/`)
  );

  const handleCalendarTimezoneSelected = useCallback(async (timezone: TimeZone) => {
    if (!currentOrNewMeeting) {
      dispatch(updateCalendarTimezone(timezone));
      return;
    }
    if (!meetingPrimaryTimezoneWouldChange(currentOrNewMeeting, timezone.name)) {
      return;
    }
    const confirmed = await confirmMeetingTimezoneChange({
      title: "Update meeting timezone?",
      message:
        "This changes the primary timezone saved on this meeting. Others who work with this meeting will see the"
        + " updated setting.",
      confirmText: "Update timezone",
      cancelText: "Cancel",
    });
    if (confirmed) {
      dispatch(updateCalendarTimezone(timezone));
    }
  }, [currentOrNewMeeting, confirmMeetingTimezoneChange, dispatch]);

  const handleSecondaryTimezoneSelected = useCallback(async (idx: number, timezone: TimeZone) => {
    const newTimezones = [
      ...secondaryTimezones.slice(0, idx), timezone, ...secondaryTimezones.slice(idx + 1)
    ].filter((tz): tz is TimeZone => tz != null);
    const newNames = newTimezones.map(tz => tz.name);

    if (!currentOrNewMeeting) {
      dispatch(updateSecondaryTimezones(newTimezones));
      return;
    }
    if (!meetingSecondaryTimezonesWouldChange(currentOrNewMeeting, newNames)) {
      return;
    }
    const confirmed = await confirmMeetingTimezoneChange({
      title: "Update additional timezones?",
      message: "This changes the additional timezones saved on this meeting. Others who work with this meeting will see"
        + " the updated setting.",
      confirmText: "Update",
      cancelText: "Cancel",
    });
    if (confirmed) {
      dispatch(updateSecondaryTimezones(newTimezones));
    }
  }, [currentOrNewMeeting, confirmMeetingTimezoneChange, dispatch, secondaryTimezones]);

  return (
    <>
      <CalendarScheduler
        selectedLeaders={selectedLeaders.map(lId => leaders.leaders.find(l => l.id === lId))
          .filter((leader): leader is Leader => !!leader)}
        calendars={calendars}
        meetings={meetings}
        calendarMap={calendarMap}
        additionalCalendars={additionalCalendars}
        newMeeting={newMeeting}
        allowedDates={allowedDates}
        coloredEvents={consolidatedEvents || []}
        showSecondaryTimezone={showSecondaryTimezone}
        currentMeetingDurationMinutes={currentMeetingDurationMinutes}
        currentMeetingAllDaySlots={currentMeetingAllDaySlots}
        meetingPreventDoubleBooking={meetingPreventDoubleBooking}
        granularTimeSelection={granularTimeSelection || false}
        leaderHasAssociations={leaderHasAssociations}
        userHasGrant={hasGrant}
        openAssociationModal={openAssociationModal}
        openAccountManagementModal={openAccountManagementModal}
        currentDateRangeInfo={currentDateRangeInfo}
        handleOpenMeeting={handleOpenMeeting}
        selectedSlots={selectedSlots}
        recurringSlots={recurringSlots}
        handleOpenRecurrenceIndex={handleOpenRecurrenceIndex}
        handleDeleteRecurrenceIndex={handleDeleteRecurrenceIndex}
        handleEditRecurrenceIndex={handleEditRecurrenceIndex}
        handleSlotsCreated={handleSlotsCreated}
        handleEditSlot={handleEditSlot}
        onDateChange={isControlled && onDateRangeChange
          ? (start: DateTime, end: DateTime) => onDateRangeChange(start, end)
          : handle(updateDateRange)}
        calendarTimezoneSelected={calendarTimezone}
        handleCalendarTimezoneSelected={handleCalendarTimezoneSelected}
        secondaryTimezonesSelected={secondaryTimezones}
        handleSecondaryTimezoneSelected={handleSecondaryTimezoneSelected}
        handleDeleteSlots={handleDeleteSlots}
        currentMeetingId={currentMeeting?.id}
        isPoll={currentOrNewMeeting?.is_poll}
        handleDuplicateMeeting={handleDuplicateMeeting}
        handleDeleteMeeting={handleDeleteMeeting}
        handleShareMeeting={handleShareMeeting}
        handleCopyLink={handleCopyLink}
        onOpenPollResults={handleOpenPollResults}
        showPendingSlots={showPendingSlots}
        condensePendingSlots={userPrefs?.condense_pending_slots ?? false}
        showAllDayEvents={showAllDayEvents}
        showMultiLeaderColumns={userPrefs?.show_multi_leader_columns ?? false}
        hideCalendarHeader={hideCalendarHeader}
        hideTodayButton={hideTodayButton}
        hideViewPicker={hideViewPicker}
        noCalendarBorder={noCalendarBorder}
        hideResourceHeader={hideResourceHeader}
        hideTimezonePicker={hideTimezonePicker}
        interactive={interactive}
        sx={sx}
        calendarView={calendarView}
        onCalendarViewChange={handleCalendarViewChange}
        calendarsLoaded={calendarsLoaded}
        leadersLoaded={leadersLoaded}
        meetingsLoaded={meetingsLoaded}
        associationsLoaded={associationsLoaded}
        weekStartDay={weekStartDay ? WeekStartDay[weekStartDay] : undefined}
        workWeekStartDay={workWeekStartDay ? WeekStartDay[workWeekStartDay] : undefined}
        meetingPrimaryTimezoneOverridesScheduleDefaults={meetingPrimaryTimezoneOverridesScheduleDefaults}
        meetingSecondaryTimezonesOverrideScheduleDefaults={meetingSecondaryTimezonesOverrideScheduleDefaults}
        onClearMeetingPrimaryTimezoneOverride={handleClearMeetingPrimaryTimezoneOverride}
        onClearMeetingSecondaryTimezoneOverride={handleClearMeetingSecondaryTimezoneOverride}
      />
      <MeetingTimezoneConfirmationModal />
    </>
  );
};

export default memo(CalendarSchedulerContainer);
